import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Gift, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../lib/api';

const RegisterPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [showBetaCode, setShowBetaCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      await register(email, password, companyName, invitationCode || '');
      toast.success('Compte cree avec succes !');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/xyron-logo.png" 
            alt="Xyron Core ERP" 
            className="w-48 h-48 mx-auto rounded-2xl mb-1 object-contain"
          />
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="text-center">
            <CardTitle className="font-heading text-xl">Creer un compte</CardTitle>
            <CardDescription>
              Commencez a gerer vos factures professionnelles
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Nom de l'entreprise</Label>
                <Input
                  id="companyName"
                  type="text"
                  placeholder="Xyron Core ERP"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  data-testid="register-company-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.ch"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
                <p className="text-xs text-slate-500">Minimum 6 caracteres</p>
              </div>

              {/* Beta code - optional toggle */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowBetaCode(!showBetaCode)}
                  className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700 transition-colors"
                  data-testid="toggle-beta-code"
                >
                  <Gift className="w-3.5 h-3.5" />
                  <span>Vous avez un code d'invitation ?</span>
                  {showBetaCode ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                {showBetaCode && (
                  <div className="mt-2 space-y-1.5">
                    <Input
                      id="invitationCode"
                      type="text"
                      placeholder="Entrez votre code"
                      value={invitationCode}
                      onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                      data-testid="register-invitation-code"
                      className="border-purple-200 focus:border-purple-400 font-mono tracking-wider"
                    />
                    <p className="text-xs text-purple-500">7 jours d'essai Pro gratuit avec un code valide</p>
                  </div>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creation...
                  </>
                ) : (
                  'Creer mon compte'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-slate-500">Deja un compte ? </span>
              <Link 
                to="/login" 
                className="text-blue-600 hover:text-blue-700 font-medium"
                data-testid="login-link"
              >
                Se connecter
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
